import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, ArrowRight, BookOpen } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import Icon from "@/components/site/Icon";
import ServiceDecor from "@/components/site/ServiceDecor";
import PageSeo from "@/components/site/PageSeo";
import { SERVICES } from "@/constants/testIds";
import { useWebsiteContent } from "@/hooks/useWebsiteContent";
import { useServices } from "@/hooks/useServices";
import { fetchGuideSlugMap } from "@/lib/guidesApi";
import { trackCtaClick, trackEvent } from "@/lib/ga";
import CountryCoverage from "@/components/services/CountryCoverage";

/**
 * Services page — conversion-focused.
 *
 * Each Service is a compact card with:
 *   - Icon + title + short commercial description
 *   - Business value box
 *   - Two CTAs:
 *       Learn More      → dedicated Pillar Guide at /guides/{slug}
 *       Discuss Service → /contact with pre-filled topic
 *
 * Educational Related Articles are intentionally NOT rendered here —
 * they live on the Guide page instead. This keeps Services focused on
 * conversion while the Guide is the deep knowledge hub.
 */
export default function Services() {
  const { hash } = useLocation();
  const { data: wc } = useWebsiteContent();
  const { services } = useServices();
  const [guideSlugMap, setGuideSlugMap] = useState({});

  useEffect(() => {
    fetchGuideSlugMap().then(setGuideSlugMap).catch(() => setGuideSlugMap({}));
  }, []);

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) {
        setTimeout(
          () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
          60,
        );
      }
    }
  }, [hash]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const servicesList = Array.isArray(services) ? services : [];
  const structuredSchemas = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
        { "@type": "ListItem", position: 2, name: "Services" },
      ],
    },
    servicesList.length > 0 && {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Manufacturing and Trading Services",
      numberOfItems: servicesList.length,
      itemListElement: servicesList.map((s, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Service",
          name: s.title,
          description: s.shortDescription || s.description || undefined,
          url: guideSlugMap[s.slug]
            ? `${origin}/guides/${guideSlugMap[s.slug]}`
            : `${origin}/services#${s.slug}`,
          serviceType: s.title,
          areaServed: [
            { "@type": "Place", name: "Southeast Asia" },
            { "@type": "Country", name: "Vietnam" },
            { "@type": "Country", name: "Indonesia" },
            { "@type": "Country", name: "Thailand" },
            { "@type": "Country", name: "Malaysia" },
            { "@type": "Country", name: "Philippines" },
          ],
          // Reference the site-wide Organization node emitted by SiteSchemas
          // via its @id — never duplicate Organization data at the page level.
          provider: { "@id": `${origin}/#organization` },
        },
      })),
    },
  ].filter(Boolean);

  return (
    <main data-testid={SERVICES.root}>
      <PageSeo
        path="/services"
        title="Services — Sourcing, manufacturing, QC and trading in Asia"
        description="A complete execution stack for companies operating across Asia — product sourcing, supplier verification, manufacturing partnerships, production management, quality control and international trading."
        schemas={structuredSchemas}
      />
      {/* HEADER ========================================================== */}
      <section className="border-b border-[#ececef] mts-soft-gradient relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 mts-dot-grid opacity-30 [mask-image:radial-gradient(60%_60%_at_50%_50%,black,transparent)]"
        />
        <div className="relative max-w-[1280px] mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#86868b]">
              {wc?.servicesEyebrow || ""}
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-6 text-[44px] sm:text-[60px] md:text-[80px] font-semibold tracking-[-0.03em] leading-[1.02] text-[#0a0a0a] max-w-4xl text-balance">
              <span
                className="italic font-normal"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                {wc?.servicesTitleItalic || ""}
              </span>{" "}
              {wc?.servicesTitleSuffix || ""}
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-8 text-[17px] md:text-[19px] leading-relaxed text-[#424245] max-w-2xl text-pretty">
              {wc?.servicesSubtitle || ""}
            </p>
          </Reveal>
        </div>
      </section>

      {/* SERVICE LIST ==================================================== */}
      <section className="bg-white">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="grid grid-cols-1 gap-6">
            {services.map((s, i) => (
              <Reveal key={s.slug} delay={i * 30}>
                <ServiceCard
                  service={s}
                  guideSlug={guideSlugMap[s.slug]}
                  valueLabel={wc?.servicesCardValueLabel || "Business value"}
                  discussLabel={wc?.servicesCardCtaLabel || "Discuss this service"}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COUNTRY COVERAGE ================================================ */}
      <CountryCoverage />

      {/* CTA — dark panel ================================================ */}
      <section className="relative overflow-hidden">
        <div className="mts-dark-panel">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-24 md:py-32">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
              <div className="md:col-span-8">
                <Reveal>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-white/60">
                    {wc?.servicesBottomEyebrow || ""}
                  </p>
                </Reveal>
                <Reveal delay={80}>
                  <h2 className="mt-6 text-[36px] sm:text-[48px] md:text-[64px] font-semibold tracking-[-0.03em] text-white leading-[1.05] max-w-3xl text-balance">
                    {wc?.servicesBottomTitlePrefix || ""}{" "}
                    <span
                      className="italic font-normal text-white/90"
                      style={{ fontFamily: "'Instrument Serif', serif" }}
                    >
                      {wc?.servicesBottomTitleItalic || ""}
                    </span>
                  </h2>
                </Reveal>
                <Reveal delay={140}>
                  <p className="mt-6 text-[16px] leading-relaxed text-white/70 max-w-xl">
                    {wc?.servicesBottomBody || ""}
                  </p>
                </Reveal>
              </div>
              <div className="md:col-span-4 md:text-right">
                <Reveal delay={200}>
                  <Link
                    to="/contact"
                    className="mts-btn-inverse"
                    onClick={() => trackCtaClick(wc?.servicesBottomCtaLabel || "Start a Project", "services-bottom")}
                  >
                    {wc?.servicesBottomCtaLabel || "Start a Project"} <ArrowRight size={16} />
                  </Link>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

/**
 * Conversion-focused Service card. Two actions:
 *   1) Learn More — always opens the corresponding Pillar Guide.
 *   2) Discuss Service — opens the contact flow with topic pre-filled.
 *
 * `guideSlug` is resolved from a single Guides API call at mount time so
 * every Learn More link points at the correct /guides/{slug} URL.
 */
function ServiceCard({ service: s, guideSlug, valueLabel, discussLabel }) {
  const guideHref = `/guides/${guideSlug || `${s.slug}-guide`}`;
  const contactHref = `/contact?topic=${encodeURIComponent(s.title || "")}&source=${encodeURIComponent(`services/${s.slug}`)}`;
  return (
    <article
      id={s.slug}
      data-testid={SERVICES.card(s.slug)}
      className="mts-card relative overflow-hidden scroll-mt-24 p-8 md:p-12"
    >
      {/* Decorative corner motif — unique per service */}
      <div className="absolute -top-4 -right-4 md:top-2 md:right-4 pointer-events-none opacity-70">
        <ServiceDecor slug={s.slug} />
      </div>

      <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
        {/* left: icon + title */}
        <div className="md:col-span-4">
          <div className="flex items-center gap-4">
            <div className="mts-icon-chip shrink-0">
              <Icon name={s.icon} size={22} strokeWidth={1.5} />
            </div>
            <h2 className="text-[22px] md:text-[26px] font-medium tracking-[-0.02em] text-[#0a0a0a] leading-[1.15]">
              {s.title}
            </h2>
          </div>
        </div>

        {/* middle: commercial copy */}
        <div className="md:col-span-5">
          <p className="text-[16px] leading-relaxed text-[#0a0a0a] font-medium tracking-tight">
            {s.summary}
          </p>
          {s.detail && (
            <p className="mt-4 text-[14px] leading-relaxed text-[#6e6e73]">
              {s.detail}
            </p>
          )}
        </div>

        {/* right: business value + dual CTAs */}
        <div className="md:col-span-3">
          {s.value && (
            <div className="rounded-2xl bg-[#f7f7f8] border border-[#ececef] p-5">
              <p className="text-[11px] uppercase tracking-[0.15em] text-[#86868b]">
                {valueLabel}
              </p>
              <p className="mt-3 text-[13.5px] leading-relaxed text-[#0a0a0a]">
                {s.value}
              </p>
            </div>
          )}

          {/* Dual CTAs — Read Complete Guide (primary, dark pill) + Discuss (link). */}
          <div className="mt-6 flex flex-col gap-3">
            <Link
              to={guideHref}
              onClick={() =>
                trackCtaClick("Read Complete Guide", `services-card:${s.slug}`, {
                  service_slug: s.slug,
                  guide_slug: guideSlug || `${s.slug}-guide`,
                  cta_variant: "read-complete-guide",
                })
              }
              data-testid={`service-learn-more-cta-${s.slug}`}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0a0a0a] text-white px-4 py-2 text-[13px] font-medium hover:opacity-90 transition-opacity"
            >
              <BookOpen size={14} strokeWidth={1.75} />
              Read Complete Guide
              <ArrowRight size={14} />
            </Link>
            <Link
              to={contactHref}
              onClick={() =>
                trackCtaClick(discussLabel, `services-card:${s.slug}`, {
                  service_slug: s.slug,
                  cta_variant: "discuss-service",
                })
              }
              data-testid={SERVICES.cardCta(s.slug)}
              className="inline-flex items-center justify-center gap-1 text-[13px] font-medium text-[#0a0a0a] hover:opacity-70 transition-opacity"
            >
              {discussLabel} <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}
